/**
 * Aisle: PDP matching upsell ("Made to Match") — behaviour for blocks/pdp-matching-upsell.liquid
 *
 * - Cards follow the colour selected on the PDP (updates live when the shopper changes colour)
 * - A card hides when its product isn't offered in that colour, or every size in that colour is sold out
 * - The whole block hides when no cards are left for that colour
 * - Dropdown shows sizes only; "Add to cart" is greyed out until a size is picked
 * - Add to cart via AJAX, refreshes the mini cart, tags the line for reporting,
 *   and publishes a "made_to_match_add_to_cart" analytics event
 */

if (!customElements.get('aisle-product-picks')) {
  class AisleProductPicks extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.eventsModule = this.dataset.eventsUrl
        ? import(this.dataset.eventsUrl).catch(() => null)
        : Promise.resolve(null);

      this.track = this.querySelector('[data-track]');
      this.nav = this.querySelector('.aisle-picks__nav');
      this.prev = this.querySelector('.aisle-picks__arrow[data-dir="-1"]');
      this.next = this.querySelector('.aisle-picks__arrow[data-dir="1"]');

      this.querySelectorAll('.aisle-picks__arrow').forEach((btn) =>
        btn.addEventListener('click', () => this.scrollByCard(Number(btn.dataset.dir)))
      );
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      this.onResize = () => this.updateArrows();
      window.addEventListener('resize', this.onResize);

      // Read each card's variant data
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.cards.forEach((card) => {
        try {
          card.variants = JSON.parse(card.querySelector('[data-variants]')?.textContent || '[]');
        } catch (_) {
          card.variants = [];
        }
        card.colorIndex = Number(card.dataset.colorIndex ?? -1);
        card.sizeIndex = Number(card.dataset.sizeIndex ?? -1);
        card.fixedIndexes = (card.dataset.fixedIndexes || '')
          .split(',')
          .filter((v) => v !== '')
          .map(Number);

        const select = card.querySelector('[data-variant-select]');
        if (select) select.addEventListener('change', () => this.onSizeChange(card));
        this.setupDropdown(card);
      });

      // Close an open size dropdown on outside click, Escape, resize or carousel scroll
      this.onDocPointer = (e) => {
        if (this.openCard && !this.openCard.dd.root.contains(e.target)) this.closeDropdown(this.openCard);
      };
      this.onDocKey = (e) => {
        if (e.key === 'Escape' && this.openCard) {
          const card = this.openCard;
          this.closeDropdown(card);
          card.dd.trigger.focus();
        }
      };
      // Page scroll: keep the list attached to its box. Resize or carousel swipe: close it.
      this.onWindowScroll = () => {
        if (this.openCard) this.positionPanel(this.openCard);
      };
      this.onWindowChange = () => {
        if (this.openCard) this.closeDropdown(this.openCard);
      };
      document.addEventListener('pointerdown', this.onDocPointer);
      document.addEventListener('keydown', this.onDocKey);
      window.addEventListener('resize', this.onWindowChange);
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      this.track.addEventListener('scroll', this.onWindowChange, { passive: true });

      this.applyColor(this.dataset.initialColor || '');

      if (this.dataset.matchColor === 'true') {
        this.onVariantUpdate = (e) => this.matchColor(e.detail?.resource?.options);
        document.addEventListener('variant:update', this.onVariantUpdate);
      }

      // Plain buttons (not form submits): the PDP blocks form submits inside it until a PDP size is chosen
      this.querySelectorAll('[data-picks-form] [data-atc]').forEach((btn) =>
        btn.addEventListener('click', (e) => this.onSubmit(e))
      );
    }

    disconnectedCallback() {
      if (this.openCard) this.closeDropdown(this.openCard);
      document.removeEventListener('pointerdown', this.onDocPointer);
      document.removeEventListener('keydown', this.onDocKey);
      window.removeEventListener('resize', this.onWindowChange);
      window.removeEventListener('scroll', this.onWindowScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.onVariantUpdate) document.removeEventListener('variant:update', this.onVariantUpdate);
      this.initialized = false;
    }

    /* ---------- carousel ---------- */

    scrollByCard(dir) {
      const slide = this.track.querySelector('.aisle-picks__slide:not([hidden])');
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: dir * (slide.offsetWidth + gap), behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.prev || !this.next) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      this.prev.disabled = scrollLeft <= 2;
      this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
    }

    /* ---------- colour matching ---------- */

    matchColor(options) {
      const colorIndex = Number(this.dataset.colorIndex);
      if (!Array.isArray(options) || colorIndex < 0) return;
      const color = options[colorIndex];
      if (color) this.applyColor(color);
    }

    applyColor(color) {
      const max = Number(this.dataset.max) || 8;
      const colsDesktop = Number(this.dataset.colsDesktop) || 3;
      let shown = 0;

      this.cards.forEach((card) => {
        const ci = card.colorIndex;
        const filter = Boolean(color) && ci >= 0;
        const inColor = filter ? card.variants.filter((v) => v.options[ci] === color) : card.variants;

        // Keep the card's own absorbency / pack size, so the dropdown is sizes only
        const base = inColor.find((v) => v.available) || inColor[0];
        const pool = base
          ? inColor.filter((v) => card.fixedIndexes.every((i) => v.options[i] === base.options[i]))
          : [];
        const hasStock = pool.some((v) => v.available);
        const show = hasStock && shown < max;

        card.hidden = !show;
        if (show) shown += 1;

        card.pool = pool;
        card.filter = filter;
        this.buildSizeOptions(card);
      });

      // Hide the whole block when nothing is left for this colour
      this.hidden = shown === 0;
      if (this.nav) this.nav.toggleAttribute('data-hide-desktop', shown <= colsDesktop);
      this.track.scrollLeft = 0;
      this.updateArrows();
    }

    /** Size only (colour is added only when colour matching is off) */
    labelFor(card, v) {
      const ci = card.colorIndex;
      const size = card.sizeIndex >= 0 ? v.options[card.sizeIndex] : '';
      if (!size) return v.title;
      return !card.filter && ci >= 0 ? `${v.options[ci]} / ${size}` : size;
    }

    /** Rebuild the dropdown for the card's colour, keeping the chosen size if it's still in stock */
    buildSizeOptions(card) {
      const select = card.querySelector('[data-variant-select]');
      const ci = card.colorIndex;
      const pool = card.pool || card.variants;

      if (select) {
        const previous = card.variants.find((v) => String(v.id) === select.value);
        const sameSize = (v) =>
          previous &&
          (card.sizeIndex >= 0
            ? v.options[card.sizeIndex] === previous.options[card.sizeIndex]
            : v.options.every((opt, i) => i === ci || opt === previous.options[i]));
        const keep = previous ? pool.find((v) => v.available && sameSize(v)) : null;

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        placeholder.textContent = this.dataset.selectLabel || 'Select a size';

        const options = pool.map((v) => {
          const o = document.createElement('option');
          o.value = String(v.id);
          o.disabled = !v.available;
          const label = this.labelFor(card, v);
          o.textContent = v.available ? label : `${label} - ${this.dataset.soldOut}`;
          return o;
        });

        select.replaceChildren(placeholder, ...options);
        select.value = keep ? String(keep.id) : '';
        if (!keep) placeholder.selected = true;
        this.renderDropdown(card);
      }

      const chosen = select ? card.variants.find((v) => String(v.id) === select.value) : null;
      const display = chosen || pool.find((v) => v.available) || pool[0];
      this.updateCard(card, chosen, display);
    }

    onSizeChange(card) {
      const select = card.querySelector('[data-variant-select]');
      const chosen = card.variants.find((v) => String(v.id) === select.value);
      this.renderDropdown(card);
      this.updateCard(card, chosen, chosen);
    }

    /* ---------- size dropdown: inline list under the box (desktop + mobile) ---------- */

    setupDropdown(card) {
      const root = card.querySelector('[data-dd]');
      if (!root) return;
      card.dd = {
        root,
        select: root.querySelector('[data-variant-select]'),
        trigger: root.querySelector('[data-dd-trigger]'),
        value: root.querySelector('[data-dd-value]'),
        panel: root.querySelector('[data-dd-panel]'),
        list: root.querySelector('[data-dd-list]'),
      };
      const { trigger, list } = card.dd;

      trigger.addEventListener('click', () =>
        this.openCard === card ? this.closeDropdown(card) : this.openDropdown(card)
      );
      trigger.addEventListener('keydown', (e) => {
        if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
          e.preventDefault();
          this.openDropdown(card);
        }
      });

      list.addEventListener('click', (e) => {
        const row = e.target.closest('[data-dd-row]');
        if (row) this.chooseRow(card, row);
      });
      list.addEventListener('keydown', (e) => {
        const rows = [...list.querySelectorAll('[data-dd-row]:not([aria-disabled="true"])')];
        const i = rows.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          rows[Math.min(i + 1, rows.length - 1)]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          rows[Math.max(i - 1, 0)]?.focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (document.activeElement?.matches('[data-dd-row]')) this.chooseRow(card, document.activeElement);
        } else if (e.key === 'Tab') {
          this.closeDropdown(card);
        }
      });
    }

    /** Build the visible rows from the hidden select (which buildSizeOptions keeps up to date) */
    renderDropdown(card) {
      if (!card.dd) return;
      const { select, list, value } = card.dd;
      const rows = [...select.options]
        .filter((o) => o.value)
        .map((o) => {
          const li = document.createElement('li');
          li.className = 'aisle-dd__row' + (o.disabled ? ' aisle-dd__row--unavailable' : '');
          li.setAttribute('role', 'option');
          li.setAttribute('data-dd-row', '');
          li.dataset.value = o.value;
          li.tabIndex = -1;
          li.setAttribute('aria-selected', String(o.value === select.value));
          if (o.disabled) li.setAttribute('aria-disabled', 'true');
          li.innerHTML =
            '<svg class="aisle-dd__check" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          li.append(document.createTextNode(o.textContent));
          return li;
        });
      list.replaceChildren(...rows);

      const selected = [...select.options].find((o) => o.value && o.value === select.value);
      value.textContent = selected ? selected.textContent : this.dataset.selectLabel || 'Select a size';
      value.classList.toggle('is-placeholder', !selected);
    }

    chooseRow(card, row) {
      if (row.getAttribute('aria-disabled') === 'true') return;
      const { select, trigger } = card.dd;
      select.value = row.dataset.value;
      this.onSizeChange(card);
      this.closeDropdown(card);
      trigger.focus({ preventScroll: true });
    }

    openDropdown(card) {
      if (!card.dd) return;
      if (this.openCard && this.openCard !== card) this.closeDropdown(this.openCard);
      const { root, trigger, panel, list } = card.dd;

      this.openCard = card;
      root.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.positionPanel(card);

      // Scroll the list (not the page) so the chosen size is visible
      const selected = list.querySelector('[aria-selected="true"]');
      panel.scrollTop = selected ? Math.max(0, selected.offsetTop - selected.offsetHeight) : 0;

      const target = selected || list.querySelector('[data-dd-row]:not([aria-disabled="true"])');
      target?.focus({ preventScroll: true });
    }

    /** Fixed position under the box (or above it when there isn't room), so the carousel can't clip it */
    positionPanel(card) {
      const { root, panel } = card.dd;
      const rect = root.getBoundingClientRect();
      panel.style.left = `${rect.left}px`;
      panel.style.width = `${rect.width}px`;
      const height = panel.offsetHeight;
      const below = window.innerHeight - rect.bottom;
      const openUp = below < height + 8 && rect.top > below;
      panel.style.top = `${openUp ? rect.top - height - 4 : rect.bottom + 4}px`;
    }

    closeDropdown(card) {
      if (!card?.dd) return;
      const { root, trigger, panel } = card.dd;
      if (this.openCard === card) this.openCard = null;
      root.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    }

    /** chosen = the variant that will be added (null until a size is picked); display = for image/price/colour */
    updateCard(card, chosen, display) {
      const form = card.querySelector('[data-picks-form]');
      const select = card.querySelector('[data-variant-select]');
      const btn = form.querySelector('[data-atc]');

      // Single-variant products don't need a size
      if (select) {
        form.querySelector('[data-variant-input]').value = chosen ? chosen.id : '';
        // Greyed-out "Add to cart" until a size is chosen
        if (!btn.hasAttribute('aria-busy')) btn.disabled = !chosen;
      }

      if (!display) return;

      // Image + title link to this colour
      if (card.dataset.url) {
        card.querySelectorAll('[data-card-link]').forEach((a) => {
          a.href = `${card.dataset.url}?variant=${display.id}`;
        });
      }

      const colorEl = card.querySelector('[data-color-label]');
      if (colorEl && card.colorIndex >= 0) colorEl.textContent = display.options[card.colorIndex] || '';

      const img = form.querySelector('[data-card-img]');
      if (img && display.image && img.getAttribute('src') !== display.image) {
        img.srcset = display.srcset || '';
        img.src = display.image;
      }

      const price = form.querySelector('[data-price]');
      price.innerHTML = display.compare
        ? `<span class="visually-hidden">${this.dataset.saleLabel}&nbsp;</span><span class="price price--sale">${display.price}</span>
           <span class="visually-hidden">${this.dataset.regularLabel}&nbsp;</span><s class="compare-at-price">${display.compare}</s>`
        : `<span class="price">${display.price}</span>`;
    }

    /* ---------- add to cart ---------- */

    async onSubmit(e) {
      e.preventDefault();
      const form = e.currentTarget.closest('[data-picks-form]');
      if (!form) return;
      const btn = form.querySelector('[data-atc]');
      const errorEl = form.querySelector('[data-error]');
      const variantId = Number(form.querySelector('[data-variant-input]').value);
      const root = window.Shopify?.routes?.root || '/';

      if (!variantId) {
        form.querySelector('[data-dd-trigger]')?.focus();
        return;
      }

      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      errorEl.hidden = true;

      // Ask Shopify to return fresh HTML for any cart sections on the page (cart drawer, etc.)
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                // Underscore = hidden in cart/checkout, but saved on the order line for reporting
                properties: {
                  _upsell_source: this.dataset.trackLabel || 'Made to Match',
                  _upsell_from: this.dataset.sourceProduct || '',
                },
              },
            ],
            ...(sectionIds.length && { sections: sectionIds.join(',') }),
          }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');

        const cart = await fetch(`${root}cart.js`).then((r) => r.json());
        const mod = await this.eventsModule;
        const detail = {
          source: 'aisle-product-picks',
          itemCount: cart.item_count,
          productId: String(data.items?.[0]?.product_id ?? ''),
          variantId: String(variantId),
          sections: data.sections,
        };
        const evt = mod?.CartAddEvent
          ? new mod.CartAddEvent(cart, this.id, detail)
          : Object.assign(new Event('cart:update', { bubbles: true }), {
              detail: { resource: cart, sourceId: this.id, data: detail },
            });
        document.dispatchEvent(evt);

        // Custom analytics event (usable in Customer events / GA4 pixels)
        try {
          const line = data.items?.[0];
          window.Shopify?.analytics?.publish?.('made_to_match_add_to_cart', {
            source: this.dataset.trackLabel,
            from_product: this.dataset.sourceProduct,
            product_title: line?.product_title,
            variant_id: line?.variant_id,
            variant_title: line?.variant_title,
            price: (line?.final_price ?? line?.price ?? 0) / 100,
            currency: cart.currency,
          });
        } catch (_) {}

        btn.textContent = btn.dataset.addedLabel;
        btn.classList.add('is-added');
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.textContent = btn.dataset.label;
        }, 2000);
      } catch (err) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } finally {
        btn.disabled = !form.querySelector('[data-variant-input]').value;
        btn.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('aisle-product-picks', AisleProductPicks);
}
